import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { AdminUserFilter } from '../models/admin-user-filter';
import { PatientFilter } from '../../shared/models/patient-filter';

/** Default "Date Range" selection (rolling 30-day window). */
export const DEFAULT_DATE_RANGE = '30';

/** Default rows per page. */
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Holds the Audit Log table state — filters, current page, and page size — so the
 * user's view survives in-app navigation into a row's detail screen and back. Because
 * the service is provided in root it lives for the whole SPA session, but it resets
 * itself to defaults the moment the user navigates out of the audit-log area
 * (anything other than the log list or one of its drill-downs), so re-entering the
 * area later starts fresh. A full page refresh recreates the service, which also resets it.
 * The ActivityLogs component binds its fields directly to these signals.
 */
@Injectable({
  providedIn: 'root',
})
export class AuditLogFilterStateService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  // Every filter is applied server-side; ActivityLogs reissues the search from page 1
  // when any of these change (except currentPage/pageSize navigation).
  readonly selectedDateRange = signal(DEFAULT_DATE_RANGE);
  readonly selectedActionType = signal('all');
  readonly flaggedOnly = signal(false);
  readonly patientFilter = signal<PatientFilter | null>(null);
  // Mutually exclusive with patientFilter (the API rejects mixing patient and admin-user
  // criteria); ActivityLogs clears one when the other is applied.
  readonly adminUserFilter = signal<AdminUserFilter | null>(null);

  readonly currentPage = signal(1);
  readonly pageSize = signal(DEFAULT_PAGE_SIZE);

  /** Whether the previous navigation left us inside the audit-log area. */
  private wasInArea = this.isAuditLogUrl(this.router.url);

  constructor() {
    // Reset the moment the user leaves the audit-log area, so a later return starts
    // fresh; navigation within the area (list <-> a row's detail) preserves state.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const inArea = this.isAuditLogUrl(event.urlAfterRedirects);
        if (this.wasInArea && !inArea) {
          this.reset();
        }
        this.wasInArea = inArea;
      });
  }

  /** Restore every field to its default (a fresh audit-log view). */
  reset(): void {
    this.selectedDateRange.set(DEFAULT_DATE_RANGE);
    this.selectedActionType.set('all');
    this.flaggedOnly.set(false);
    this.patientFilter.set(null);
    this.adminUserFilter.set(null);
    this.currentPage.set(1);
    this.pageSize.set(DEFAULT_PAGE_SIZE);
  }

  /**
   * The audit-log area is the log list and every drill-down opened from it. All three detail
   * routes must be listed: the unified feed mixes disclosure and admin-action rows, and a
   * user/employee filter narrows it to admin actions, so omitting `/admin-action-detail`
   * would reset the filters on the way into the detail the filter led the user to.
   *
   * Adding a drill-down route to `app.routes.ts` means adding it here too — a row that opens a
   * screen this list doesn't know about silently wipes the filters that led the user to it, with
   * no error to notice. Keep in sync with `App.auditLogSectionActive`, which lists the same set to
   * keep the nav item highlighted across the area.
   */
  private isAuditLogUrl(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return (
      path.startsWith('/activity-logs') ||
      path.startsWith('/disclosure-detail') ||
      path.startsWith('/admin-action-detail') ||
      path.startsWith('/appointment-detail')
    );
  }
}
